import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import dayjs from 'dayjs';
import classes from './CustomCalendarPicker.module.scss';
import colors from '../../../scss/colors';
import { calendarTypes } from '../../../utils/constants';
import { generalEnums } from '../../../utils/enums';

const monthsList = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export default function CustomCalendarPicker({
  dateDisablingAbility = false,
  daysAvailability = [],
  userDate = dayjs(),
  setUserDate,
  calendarType,
  backgroundColor = colors.PAGE_BACKGROUND,
  disabledBeforeIncluded,
  dateBackAction = () => {},
  dateForwardAction = () => {},
  sx,
  loading = false,
  isMonthYearPicker = false,
  minDate = dayjs('1950-01-01'),
  maxDate = dayjs(),
}) {
  const [dateToShow, setDateToShow] = useState(dayjs());

  const yearsList = useMemo(() => {
    const startYear = minDate.year();
    const endYear = maxDate.year();
    const years = [];

    for (let i = startYear; i <= endYear; i++) {
      years.push(i);
    }

    return years;
  }, [minDate, maxDate]);

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value, 10);
    setDateToShow((prev) => prev.clone().month(newMonth));
  }

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value, 10);
    setDateToShow((prev) => prev.clone().year(newYear));
  }

  const currentDay = useMemo(() => dayjs().toDate(), []);

  const firstDayOfTheMonth = useMemo(() => dateToShow.clone().startOf('month'), [dateToShow]);

  const firstDayOfFirstWeekOfMonth = useMemo(() => {
    if (firstDayOfTheMonth.day() === 0) {
      return dayjs(firstDayOfTheMonth.add(-1, 'day')).startOf('week');
    }
    return dayjs(firstDayOfTheMonth).startOf('week');
  }, [firstDayOfTheMonth]);

  const generateFirstDayOfEachWeek = useCallback((day) => {
    const dates = [day];
    let firstDate;
    for (let i = 1; i < 6; i++) {
      const dateFD = day.clone().add(i, 'week');
      if (dateFD.get('date') === 1) {
        firstDate = dateFD;
      }
      if (
        i === 5 &&
        dateFD.add(3, 'day').month() !== dates[1].month() &&
        firstDate?.day() !== 0 &&
        firstDate?.day() !== 6
      )
        break;
      dates.push(dateFD);
    }
    return dates;
  }, []);

  const generateWeek = useCallback((day) => {
    const dates = [];
    for (let i = 1; i < 8; i++) {
      const dateW = day.clone().add(i, 'day').toDate();
      dates.push(dateW);
    }
    return dates;
  }, []);

  const generateWeeksOfTheMonth = useMemo(() => {
    const firstDayOfEachWeek = generateFirstDayOfEachWeek(firstDayOfFirstWeekOfMonth);
    return firstDayOfEachWeek.map((dateFDEW) => generateWeek(dateFDEW));
  }, [generateFirstDayOfEachWeek, firstDayOfFirstWeekOfMonth, generateWeek]);

  const getClassForVariant = (day) => {
    if (dateToShow.clone().toDate().getMonth() !== day.getMonth()) {
      return classes.nextMonth;
    }
    if (dayjs(currentDay).isSame(day, 'date')) {
      return classes.today;
    }

    if (dayjs(userDate).isSame(day) && calendarType === calendarTypes.DAY) {
      return classes.selected;
    }

    return classes.default;
  };

  const getClassWeekVariant = (week) => {
    if (calendarType === calendarTypes.WEEK) {
      return week.some((item) => dayjs(item.toString()).isSame(userDate, 'date'))
        ? `${classes.calendarWeekWrapper} ${classes.selected}`
        : classes.calendarWeekWrapper;
    }

    return classes.calendarDayWrapper;
  };

  const isDisabled = (day) => {
    if (minDate && dayjs(day).isBefore(minDate, 'day')) return true;
    if (maxDate && dayjs(day).isAfter(maxDate, 'day')) return true;
    if (disabledBeforeIncluded && dayjs(day).isBefore(disabledBeforeIncluded)) return true;
    return false;
  };

  const isDayDisabled = (day) =>
    dateDisablingAbility && !daysAvailability.includes(dayjs(day).format(generalEnums.DATEFORMAT));

  const handlePreviousCalendar = () => {
    setDateToShow((dateBack) => {
      const date = dateBack.subtract(1, 'month');
      dateBackAction(date.format(generalEnums.DATEFORMAT));
      return date;
    });
  };

  const handleNextCalendar = () => {
    setDateToShow((dateForward) => {
      const date = dateForward.add(1, 'month');
      dateForwardAction(date.format(generalEnums.DATEFORMAT));
      return date;
    });
  };

  useEffect(() => {
    if (dateToShow.month() === dayjs().month() && userDate && dateDisablingAbility) {
      setDateToShow(userDate);
    }
  }, []);

  return (
    <div className={classes.mainWrapper} style={{ backgroundColor, ...sx, zIndex: 1000000 }}>
      <div className={classes.calendarHeaderWrapper}>
        <div className={classes.actionButtons}>
          {isMonthYearPicker ? (
            <div className='flex gap-2 items-center'>
              <select
                value={dateToShow.month()}
                onChange={handleMonthChange}
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontFamily: '"Airswift Radius", sans-serif',
                }}
                >
                {monthsList.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
                </select>
              <select
                value={dateToShow.year()}
                onChange={handleYearChange}
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontFamily: '"Airswift Radius", sans-serif',
                }}
              >
                {yearsList.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <ArrowBackIosNewIcon fontSize="small" onClick={handlePreviousCalendar} sx={{ color: colors.LIGHT_GREY }} />
              <p>{dateToShow.clone().format('MMMM, YYYY')}</p>
              <ArrowForwardIosIcon fontSize="small" onClick={handleNextCalendar} sx={{ color: colors.LIGHT_GREY }} />
            </>
          )}
        </div>
      </div>
      <div className={classes.weekDaysWrapper}>
        {generateWeeksOfTheMonth[0].map((day, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className={classes.weekDayCell} key={`week-day-${index}`}>
            {dayjs(day).format('ddd')}
          </div>
        ))}
      </div>
      {generateWeeksOfTheMonth.map((week) => (
        <div key={`week-${week.toString()}`} className={getClassWeekVariant(week)}>
          {week.map((day) => (
            <button
              key={`day-${day.toString()}`}
              onClick={() => {
                if (isDayDisabled(day)) return;
                if (!isDisabled(day) || !isDayDisabled(day)) setUserDate(day);
              }}
              className={`${classes.calendarDayCell} ${getClassForVariant(day)} ${
                isDisabled(day) || isDayDisabled(day) ? classes.disabled : ''
              } ${loading && classes.loadingText}`}
            >
              {day.getDate()}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}